const API_BASE_URL =
    import.meta.env.VITE_API_BASE ||
    import.meta.env.VITE_API_URL ||
    'https://hivecore-backend-staging.up.railway.app';

// ============================================================================
// Deal Criteria v0 - Transparent, Rule-Based Filtering
// NOT underwriting, NOT valuation, NOT deal scoring
// ============================================================================

/**
 * DealCriteria - all fields optional, ANDed together when filtering
 * This is a transparent filter system - every pin explains why it matched
 */
export interface DealCriteria {
    maxOpeningBid?: number;
    minOpeningBid?: number;
    auctionWithinDays?: number;
    locationConfidence?: 'high' | 'any';
    county?: string;
    hasComps?: boolean; // boolean presence only, not value
}

export type LocationType = 'centroid' | 'parcel_polygon' | 'rooftop' | 'interpolated';

export interface MapPin {
    propertyId: string;
    parcelId?: string;
    lat: number;
    lon: number;
    // Support both camelCase and snake_case from API (backend may return either)
    locationConfidence?: 'high' | 'low';
    location_confidence?: 'high' | 'low';
    locationSource?: string;
    locationType?: LocationType;
    // Foreclosure event fields (may be undefined for parcel_inventory mode)
    status?: string;
    auctionDate?: string;
    auction_date?: string;
    caseFiledDate?: string;
    case_filed_date?: string;
    openingBid?: number | string;
    opening_bid?: number | string;
    compsMedian?: number;
    deltaPct?: number;
    // Parcel inventory fields
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    ownerName?: string;
    propertyType?: string;
    assessedValue?: number;
    marketValue?: number;
    yearBuilt?: number;
    acreage?: number;
    beds?: number;
    baths?: number;
    county?: string;
    criteriaMatches?: string[];
    sqft?: number;
}

// Import canonical field types from the shared utility
import type { CanonicalFields, CanonicalLocation, CanonicalSummary } from '@/utils/canonicalFields';

// Legacy InterestedProperty (deprecated - use InterestedPropertyDTO)
export interface InterestedProperty {
    id: string;
    userId: string;
    propertyId: string;
    status: string;
    createdAt: string;
}

// Canonical InterestedPropertyDTO - Phase 5B.2D
export interface InterestedPropertyDTO {
    propertyId: string;
    parcelId?: string;
    stage: string;
    starredAt: string;
    lat?: number;
    lon?: number;
    status?: string;
    auctionDate?: string;
    openingBid?: string;
    // Legacy fields (deprecated, use canonical)
    ownerName?: string;
    propertyType?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    assessedValue?: number;
    marketValue?: number;
    yearBuilt?: number;
    acreage?: number;
    beds?: number;
    baths?: number;
    sqft?: number;
    enrichmentStatus?: string;
    conditionCode?: string;
    conditionLabel?: string;
    conditionSource?: string;
    // Canonical fields with provenance (Phase 5B.2D)
    canonical: CanonicalFields;
    location: CanonicalLocation;
    summary: CanonicalSummary;
}

// Legacy InterestedResponse (deprecated)
export interface InterestedResponse {
    success: boolean;
    userId: string;
    total: number;
    limit: number;
    offset: number;
    items: InterestedProperty[];
}

// Canonical InterestedResponse - Phase 5B.2D
export interface InterestedResponseV2 {
    success: boolean;
    userId: string;
    total: number;
    limit: number;
    offset: number;
    items: InterestedPropertyDTO[];
}

export type DismissReason =
    | 'not_investment_area'
    | 'price_too_high'
    | 'condition'
    | 'duplicate'
    | 'other';

export interface DismissedIdsResponse {
    success: boolean;
    userId: string;
    propertyIds: string[];
}
export interface InterestedIdsResponse {
    success: boolean;
    userId: string;
    propertyIds: string[];
}

export type CoverageStatus = 'in_coverage' | 'no_data' | 'out_of_coverage';

export interface CoverageMetadata {
    status: CoverageStatus;
    supportedAreas: { state: string; county: string }[];
    message?: string;
}

export interface MapPinsResponse {
    bbox: string;
    includeApproximate: boolean;
    count: number;
    pins: MapPin[];
    coverage?: CoverageMetadata;
}

export interface Alert {
    id: string;
    type: string;
    severity: 'info' | 'warning' | 'critical';
    userId: string;
    propertyId: string;
    savedSearchId?: string;
    title: string;
    message: string;
    evidence: {
        openingBid: number;
        compsMedian: number;
        deltaPct: number;
        auctionDate: string;
        locationConfidence: string;
    };
    channels: {
        inApp: boolean;
        email: boolean;
        sms: boolean;
    };
    createdAt: string;
}

export interface AlertsResponse {
    page: number;
    pageSize: number;
    total: number;
    alerts: Alert[];
}

export interface UserPreferences {
    userId: string;
    notifications: {
        emailAlerts: boolean;
        auctionReminders: boolean;
        priceDropAlerts: boolean;
        weeklyDigest: boolean;
    };
    mapPreferences: {
        showApproximateLocations: boolean;
        autoFitBounds: boolean;
        showCountyLabels: boolean;
    };
    updatedAt: string;
}

export interface PreferencesResponse {
    success: boolean;
    data: UserPreferences;
}

export type ApiState =
    | 'idle'
    | 'loading'
    | 'success'
    | 'empty'
    | 'notDeployed'
    | 'authError'
    | 'error';

export interface ApiResult<T> {
    state: ApiState;
    data: T | null;
    error: string | null;
}

interface CacheEntry<T> {
    data: T;
    timestamp: number;
}

const CACHE_TTL_MS = 60000;
const MAX_RETRIES = 3;
const INITIAL_BACKOFF_MS = 1000;
const AUTH_TOKEN_KEY = 'realsignal_access_token';

// Canonical subscription status type - backend must return one of these
export type SubscriptionStatus = 'active' | 'trial' | 'expired';

// User profile stored in auth state
export interface UserProfile {
    id: string;
    email: string;
    name: string;
    isActive: boolean;
    subscriptionStatus: SubscriptionStatus;
    subscriptionEndDate: string | null;
    trialEndsAt: string | null;
}

// Auth event types for centralized handling
export type AuthEvent = 'AUTH_REQUIRED' | 'SESSION_EXPIRED' | 'LOGGED_IN' | 'LOGGED_OUT';
type AuthEventListener = (event: AuthEvent) => void;

class RealSignalApiClient {
    private cache: Map<string, CacheEntry<unknown>> = new Map();
    private notDeployedEndpoints: Set<string> = new Set();
    private notDeployedEmitted: Set<string> = new Set();

    // Centralized auth state - single source of truth
    private currentUserProfile: UserProfile | null = null;
    private authEventListeners: Set<AuthEventListener> = new Set();
    private isHandling401 = false; // Prevent multiple 401 handlers firing

    private getAuthToken(): string | null {
        if (typeof window === 'undefined') return null;

        const localToken = localStorage.getItem(AUTH_TOKEN_KEY);
        const sessionToken = sessionStorage.getItem(AUTH_TOKEN_KEY);

        const localExp = this.getTokenExpiry(localToken);
        const sessionExp = this.getTokenExpiry(sessionToken);

        const now = Date.now();
        const localValid = localExp !== null && localExp > now;
        const sessionValid = sessionExp !== null && sessionExp > now;

        // Clean up expired tokens
        if (localToken && !localValid) {
            localStorage.removeItem(AUTH_TOKEN_KEY);
        }
        if (sessionToken && !sessionValid) {
            sessionStorage.removeItem(AUTH_TOKEN_KEY);
        }

        // If both valid, check for cross-user mismatch
        if (localValid && sessionValid) {
            const localUserId = this.getTokenUserId(localToken);
            const sessionUserId = this.getTokenUserId(sessionToken);

            // If tokens belong to different users, prefer session token (most recent login)
            // and clean up the stale localStorage token from previous user
            if (localUserId && sessionUserId && localUserId !== sessionUserId) {
                console.log('[Auth] Cross-user token mismatch detected, using session token');
                localStorage.removeItem(AUTH_TOKEN_KEY);
                return sessionToken;
            }

            // Same user (or can't determine user), choose the one that expires later
            return localExp >= sessionExp ? localToken : sessionToken;
        }

        // Return whichever is valid
        if (localValid) return localToken;
        if (sessionValid) return sessionToken;

        return null;
    }

    /**
     * Get token expiry timestamp in milliseconds, or null if invalid
     */
    private getTokenExpiry(token: string | null): number | null {
        if (!token) return null;
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.exp * 1000; // Convert to milliseconds
        } catch {
            return null; // Invalid token format
        }
    }

    /**
     * Get user ID (sub claim) from JWT token, or null if invalid
     */
    private getTokenUserId(token: string | null): string | null {
        if (!token) return null;
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.sub || payload.userId || payload.user_id || null;
        } catch {
            return null;
        }
    }

    /**
     * Check if a JWT token is valid (not expired)
     */
    private isTokenValid(token: string): boolean {
        const exp = this.getTokenExpiry(token);
        return exp !== null && Date.now() < exp;
    }

    private getAuthHeaders(): Record<string, string> {
        const token = this.getAuthToken();
        if (token) {
            return { Authorization: `Bearer ${token}` };
        }
        return {};
    }

    private getCacheKey(endpoint: string, params: Record<string, string>): string {
        const sortedParams = Object.keys(params)
            .sort()
            .map(k => `${k}=${params[k]}`)
            .join('&');
        return `${endpoint}?${sortedParams}`;
    }

    private getFromCache<T>(key: string): T | null {
        const entry = this.cache.get(key) as CacheEntry<T> | undefined;
        if (entry && Date.now() - entry.timestamp < CACHE_TTL_MS) {
            return entry.data;
        }
        this.cache.delete(key);
        return null;
    }

    private setCache<T>(key: string, data: T): void {
        this.cache.set(key, { data, timestamp: Date.now() });
    }

    private emitTelemetry(event: string, data: Record<string, unknown>): void {
        console.log(`[RealSignal Telemetry] ${event}:`, data);
    }

    // ========================================================================
    // Centralized Auth State Management
    // ========================================================================

    /**
     * Subscribe to auth events (AUTH_REQUIRED, SESSION_EXPIRED, LOGGED_IN, LOGGED_OUT)
     * Returns unsubscribe function
     */
    onAuthEvent(listener: AuthEventListener): () => void {
        this.authEventListeners.add(listener);
        return () => this.authEventListeners.delete(listener);
    }

    private emitAuthEvent(event: AuthEvent): void {
        this.authEventListeners.forEach(listener => listener(event));
    }

    /**
     * Centralized 401 handler - called once per session expiry
     * Clears session and emits SESSION_EXPIRED event
     */
    private handle401(): void {
        if (this.isHandling401) return; // Prevent multiple handlers
        this.isHandling401 = true;

        console.warn('[Auth] Session expired - clearing auth state');
        this.clearAuthState();
        this.emitAuthEvent('SESSION_EXPIRED');

        // Reset flag after a short delay to allow for new requests
        setTimeout(() => {
            this.isHandling401 = false;
        }, 1000);
    }

    /**
     * Clear all auth state (token, user profile, cache)
     * Note: Refresh tokens are now stored in httpOnly cookies (server-side),
     * but we still clear legacy localStorage/sessionStorage entries for backward compatibility
     */
    private clearAuthState(): void {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem('realsignal_refresh_token');
        sessionStorage.removeItem(AUTH_TOKEN_KEY);
        sessionStorage.removeItem('realsignal_refresh_token');
        this.currentUserProfile = null;
        this.cache.clear();
    }

    /**
     * Get the cached user profile (set during boot rehydration)
     */
    getUserProfile(): UserProfile | null {
        return this.currentUserProfile;
    }

    /**
     * Set the user profile (called after successful /me fetch)
     */
    setUserProfile(profile: UserProfile | null): void {
        this.currentUserProfile = profile;
    }

    /**
     * Map backend subscription status string to canonical type
     * Defaults to 'expired' for unknown values
     */
    private normalizeSubscriptionStatus(status: string | undefined | null): SubscriptionStatus {
        if (status === 'active' || status === 'trial' || status === 'expired') {
            return status;
        }
        // Map common variations
        if (status === 'trialing' || status === 'in_trial') return 'trial';
        if (status === 'inactive' || status === 'cancelled' || status === 'canceled')
            return 'expired';
        // Default to expired for unknown values
        console.warn(`[Auth] Unknown subscription status: ${status}, defaulting to 'expired'`);
        return 'expired';
    }

    /**
     * Boot-time auth rehydration - call this on app init
     * Reads token from storage, validates it, and fetches user profile
     */
    async rehydrateAuth(): Promise<UserProfile | null> {
        const token = this.getAuthToken();
        if (!token) {
            console.log('[Auth] No token found - user is logged out');
            this.currentUserProfile = null;
            return null;
        }

        // Validate token expiry
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const exp = payload.exp * 1000;
            if (Date.now() >= exp) {
                console.warn('[Auth] Token expired - clearing auth state');
                this.clearAuthState();
                return null;
            }
        } catch {
            console.warn('[Auth] Invalid token format - clearing auth state');
            this.clearAuthState();
            return null;
        }

        // Fetch full user profile from /me endpoint
        const result = await this.fetchCurrentUser();
        if (result.state === 'success' && result.data) {
            const profile: UserProfile = {
                ...result.data,
                subscriptionStatus: this.normalizeSubscriptionStatus(
                    result.data.subscriptionStatus
                ),
            };
            this.currentUserProfile = profile;
            this.emitAuthEvent('LOGGED_IN');
            return profile;
        }

        // If /me fails, token might be invalid
        if (result.state === 'authError') {
            this.clearAuthState();
        }

        return null;
    }

    private async fetchWithRetry<T>(
        endpoint: string,
        params: Record<string, string> = {},
        retries = MAX_RETRIES
    ): Promise<ApiResult<T>> {
        const cacheKey = this.getCacheKey(endpoint, params);

        const cached = this.getFromCache<T>(cacheKey);
        if (cached) {
            return { state: 'success', data: cached, error: null };
        }

        if (this.notDeployedEndpoints.has(endpoint)) {
            return { state: 'notDeployed', data: null, error: 'Endpoint not deployed' };
        }

        const queryString =
            Object.keys(params).length > 0
                ? '?' +
                  Object.keys(params)
                      .map(k => `${k}=${encodeURIComponent(params[k])}`)
                      .join('&')
                : '';
        const url = `${API_BASE_URL}${endpoint}${queryString}`;

        let lastError: Error | null = null;
        let attempt = 0;

        while (attempt < retries) {
            try {
                const response = await fetch(url);

                if (response.status === 404) {
                    this.notDeployedEndpoints.add(endpoint);

                    if (!this.notDeployedEmitted.has(endpoint)) {
                        this.notDeployedEmitted.add(endpoint);
                        this.emitTelemetry('endpoint_not_deployed', {
                            endpoint,
                            environment: API_BASE_URL,
                            timestamp: new Date().toISOString(),
                        });
                    }

                    return {
                        state: 'notDeployed',
                        data: null,
                        error: 'RealSignal service not deployed in this environment',
                    };
                }

                if (response.status === 401 || response.status === 403) {
                    return {
                        state: 'authError',
                        data: null,
                        error: 'Not authorized to access this resource',
                    };
                }

                if (!response.ok) {
                    throw new Error(`API error: ${response.status} ${response.statusText}`);
                }

                const data = (await response.json()) as T;
                this.setCache(cacheKey, data);

                return { state: 'success', data, error: null };
            } catch (error) {
                lastError = error instanceof Error ? error : new Error(String(error));
                attempt++;

                if (attempt < retries) {
                    const backoffMs = INITIAL_BACKOFF_MS * Math.pow(2, attempt - 1);
                    await new Promise(resolve => setTimeout(resolve, backoffMs));
                }
            }
        }

        this.emitTelemetry('api_error', {
            endpoint,
            error: lastError?.message,
            attempts: attempt,
            timestamp: new Date().toISOString(),
        });

        return {
            state: 'error',
            data: null,
            error: lastError?.message || 'Failed to fetch data',
        };
    }

    /**
     * Encode DealCriteria as base64 JSON for API query param
     */
    private encodeCriteria(criteria: DealCriteria): string {
        // Remove undefined/null values before encoding
        const cleanCriteria = Object.fromEntries(
            Object.entries(criteria).filter(([, v]) => v !== undefined && v !== null)
        );
        if (Object.keys(cleanCriteria).length === 0) {
            return '';
        }
        return btoa(JSON.stringify(cleanCriteria));
    }

    async getMapPins(
        options: {
            bbox?: string;
            includeApproximate?: boolean;
            limit?: number;
            criteria?: DealCriteria;
        } = {}
    ): Promise<ApiResult<MapPinsResponse>> {
        const params: Record<string, string> = {};

        if (options.bbox) {
            params.bbox = options.bbox;
        } else {
            params.bbox = '-106.6,25.8,-93.5,36.5';
        }

        if (options.includeApproximate !== undefined) {
            params.includeApproximate = String(options.includeApproximate);
        }

        if (options.limit !== undefined) {
            params.limit = String(options.limit);
        } else {
            params.limit = '200';
        }

        // Encode Deal Criteria as base64 JSON if provided
        if (options.criteria) {
            const encoded = this.encodeCriteria(options.criteria);
            if (encoded) {
                params.criteria = encoded;
            }
        }

        const result = await this.fetchWithRetry<MapPinsResponse>(
            '/api/realsignal/map-pins',
            params
        );

        if (result.state === 'success' && result.data) {
            if (!result.data.pins || result.data.pins.length === 0) {
                return { state: 'empty', data: result.data, error: null };
            }
        }

        return result;
    }

    async getAlerts(
        options: {
            tenant?: string;
            page?: number;
            pageSize?: number;
        } = {}
    ): Promise<ApiResult<AlertsResponse>> {
        const token = this.getAuthToken();
        if (!token) {
            return {
                state: 'authError',
                data: null,
                error: 'Authentication required - please sign in',
            };
        }

        const params: Record<string, string> = {};

        if (options.tenant) {
            params.tenant = options.tenant;
        }

        if (options.page !== undefined) {
            params.page = String(options.page);
        } else {
            params.page = '1';
        }

        if (options.pageSize !== undefined) {
            params.pageSize = String(options.pageSize);
        } else {
            params.pageSize = '20';
        }

        const queryString =
            Object.keys(params).length > 0
                ? '?' +
                  Object.keys(params)
                      .map(k => `${k}=${encodeURIComponent(params[k])}`)
                      .join('&')
                : '';
        const url = `${API_BASE_URL}/api/realsignal/alerts${queryString}`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    ...this.getAuthHeaders(),
                },
            });

            if (response.status === 401) {
                this.logout();
                return {
                    state: 'authError',
                    data: null,
                    error: 'Session expired. Please sign in again.',
                };
            }

            if (response.status === 404) {
                return {
                    state: 'notDeployed',
                    data: null,
                    error: 'Alerts endpoint not deployed',
                };
            }

            if (!response.ok) {
                throw new Error(`API error: ${response.status} ${response.statusText}`);
            }

            const data = (await response.json()) as AlertsResponse;

            if (!data.alerts || data.alerts.length === 0) {
                return { state: 'empty', data, error: null };
            }

            return { state: 'success', data, error: null };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            this.emitTelemetry('alerts_get_error', {
                error: errorMessage,
                timestamp: new Date().toISOString(),
            });
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async getPreferences(): Promise<ApiResult<PreferencesResponse>> {
        const token = this.getAuthToken();
        if (!token) {
            return {
                state: 'authError',
                data: null,
                error: 'Authentication required - please sign in',
            };
        }

        const url = `${API_BASE_URL}/api/realsignal/preferences`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    ...this.getAuthHeaders(),
                },
            });

            if (response.status === 401) {
                this.logout();
                return {
                    state: 'authError',
                    data: null,
                    error: 'Session expired. Please sign in again.',
                };
            }

            if (response.status === 404) {
                return {
                    state: 'notDeployed',
                    data: null,
                    error: 'Preferences endpoint not deployed',
                };
            }

            if (!response.ok) {
                throw new Error(`API error: ${response.status} ${response.statusText}`);
            }

            const data = (await response.json()) as PreferencesResponse;
            return { state: 'success', data, error: null };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            this.emitTelemetry('preferences_get_error', {
                error: errorMessage,
                timestamp: new Date().toISOString(),
            });
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async updatePreferences(
        updates: Partial<{
            notifications: Partial<UserPreferences['notifications']>;
            mapPreferences: Partial<UserPreferences['mapPreferences']>;
        }>
    ): Promise<ApiResult<PreferencesResponse>> {
        const token = this.getAuthToken();
        if (!token) {
            return {
                state: 'authError',
                data: null,
                error: 'Authentication required - please sign in',
            };
        }

        const url = `${API_BASE_URL}/api/realsignal/preferences`;

        try {
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders(),
                },
                body: JSON.stringify(updates),
            });

            if (response.status === 401) {
                this.logout();
                return {
                    state: 'authError',
                    data: null,
                    error: 'Session expired. Please sign in again.',
                };
            }

            if (response.status === 404) {
                return {
                    state: 'notDeployed',
                    data: null,
                    error: 'Preferences endpoint not deployed',
                };
            }

            if (!response.ok) {
                throw new Error(`API error: ${response.status} ${response.statusText}`);
            }

            const data = (await response.json()) as PreferencesResponse;

            this.cache.delete('/api/realsignal/preferences?');

            return { state: 'success', data, error: null };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            this.emitTelemetry('preferences_update_error', {
                error: errorMessage,
                timestamp: new Date().toISOString(),
            });
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    clearNotDeployedState(endpoint?: string): void {
        if (endpoint) {
            this.notDeployedEndpoints.delete(endpoint);
        } else {
            this.notDeployedEndpoints.clear();
        }
    }

    clearCache(): void {
        this.cache.clear();
    }

    /**
     * Create a Stripe Customer Portal session for billing management
     * Returns a URL to redirect the user to Stripe's hosted portal
     */
    async createBillingPortalSession(): Promise<ApiResult<{ portalUrl: string }>> {
        const token = this.getAuthToken();
        if (!token) {
            return {
                state: 'authError',
                data: null,
                error: 'Authentication required - please sign in',
            };
        }

        const url = `${API_BASE_URL}/api/realsignal/billing/portal-session`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders(),
                },
            });

            if (response.status === 401) {
                this.handle401();
                return {
                    state: 'authError',
                    data: null,
                    error: 'Session expired. Please sign in again.',
                };
            }

            if (response.status === 400) {
                const errorData = await response.json().catch(() => ({}));
                return {
                    state: 'error',
                    data: null,
                    error: errorData.error || 'No billing account found',
                };
            }

            if (!response.ok) {
                throw new Error(`API error: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();

            if (data.success && data.data?.portalUrl) {
                return { state: 'success', data: data.data, error: null };
            }

            return {
                state: 'error',
                data: null,
                error: 'Failed to create billing portal session',
            };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            this.emitTelemetry('billing_portal_error', {
                error: errorMessage,
                timestamp: new Date().toISOString(),
            });
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async login(
        email: string,
        password: string,
        rememberMe: boolean = false
    ): Promise<
        ApiResult<{
            user: { id: string; email: string };
            tokens: { accessToken: string };
        }>
    > {
        const url = `${API_BASE_URL}/api/auth/login`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ email, password }),
            });

            if (response.status === 401) {
                return {
                    state: 'authError',
                    data: null,
                    error: 'Invalid email or password',
                };
            }

            if (response.status === 403) {
                return {
                    state: 'authError',
                    data: null,
                    error: 'Account suspended',
                };
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Login failed: ${response.status}`);
            }

            const data = await response.json();

            if (data.success && data.data?.tokens) {
                const storage = rememberMe ? localStorage : sessionStorage;
                storage.setItem('realsignal_access_token', data.data.tokens.accessToken);
                storage.setItem('realsignal_remember_me', rememberMe ? 'true' : 'false');

                if (rememberMe) {
                    localStorage.setItem('realsignal_access_token', data.data.tokens.accessToken);
                }
            }

            return { state: 'success', data: data.data, error: null };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            this.emitTelemetry('login_error', {
                error: errorMessage,
                timestamp: new Date().toISOString(),
            });
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async logout(): Promise<void> {
        const token = this.getAuthToken();

        try {
            await fetch(`${API_BASE_URL}/api/auth/logout`, {
                method: 'POST',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                credentials: 'include',
            });
        } catch (error) {
            console.error('Logout API error:', error);
        }

        // Use centralized auth state clearing
        this.clearAuthState();
        localStorage.removeItem('realsignal_remember_me');
        sessionStorage.removeItem('realsignal_remember_me');
        this.emitAuthEvent('LOGGED_OUT');
    }

    async refreshToken(): Promise<ApiResult<{ accessToken: string }>> {
        const url = `${API_BASE_URL}/api/auth/refresh`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            if (!response.ok) {
                await this.logout();
                return {
                    state: 'authError',
                    data: null,
                    error: 'Session expired. Please sign in again.',
                };
            }

            const data = await response.json();

            if (data.success && data.data?.accessToken) {
                const rememberMe = localStorage.getItem('realsignal_remember_me') === 'true';
                const storage = rememberMe ? localStorage : sessionStorage;
                storage.setItem('realsignal_access_token', data.data.accessToken);

                if (rememberMe) {
                    localStorage.setItem('realsignal_access_token', data.data.accessToken);
                }
            }

            return { state: 'success', data: data.data, error: null };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    isAuthenticated(): boolean {
        const token = this.getAuthToken();
        if (!token) return false;

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const exp = payload.exp * 1000;
            return Date.now() < exp;
        } catch {
            return false;
        }
    }

    /**
     * Get the current user's ID from the JWT token.
     * Returns null if not authenticated or token is invalid.
     */
    getCurrentUserId(): string | null {
        const token = this.getAuthToken();
        if (!token) return null;

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            // Check if token is expired
            const exp = payload.exp * 1000;
            if (Date.now() >= exp) return null;
            return payload.userId || payload.sub || null;
        } catch {
            return null;
        }
    }

    /**
     * Get the current user's info from the JWT token.
     * Returns null if not authenticated or token is invalid.
     * This is the canonical source of user identity for the app.
     */
    getCurrentUser(): { userId: string; email?: string } | null {
        const token = this.getAuthToken();
        if (!token) return null;

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            // Check if token is expired
            const exp = payload.exp * 1000;
            if (Date.now() >= exp) return null;
            const userId = payload.userId || payload.sub;
            if (!userId) return null;
            return {
                userId,
                email: payload.email,
            };
        } catch {
            return null;
        }
    }

    /**
     * Fetch full user profile from the backend /auth/me endpoint.
     * Returns user identity including subscription status for Settings page.
     */
    async fetchCurrentUser(): Promise<
        ApiResult<{
            id: string;
            email: string;
            name: string;
            isActive: boolean;
            subscriptionStatus: string;
            subscriptionEndDate: string | null;
            trialEndsAt: string | null;
        }>
    > {
        const url = `${API_BASE_URL}/api/realsignal/auth/me`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders(),
                },
            });

            if (response.status === 401) {
                this.logout();
                return {
                    state: 'authError',
                    data: null,
                    error: 'Not authenticated',
                };
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to fetch user info');
            }

            const data = await response.json();

            if (data.success && data.data) {
                return {
                    state: 'success',
                    data: data.data,
                    error: null,
                };
            } else {
                throw new Error(data.error || 'Failed to fetch user info');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    /**
     * Initialize demo session for staging/dev environments.
     * Calls the demo/session endpoint to get a server-issued JWT.
     * Only works when REAL_SIGNAL_DEMO_MODE=1 is set on the backend.
     */
    async initDemoSession(): Promise<ApiResult<{ accessToken: string; userId: string }>> {
        const url = `${API_BASE_URL}/api/realsignal/demo/session`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.status === 403) {
                const errorData = await response.json().catch(() => ({}));
                return {
                    state: 'authError',
                    data: null,
                    error: errorData.error || 'Demo mode not enabled',
                };
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to initialize demo session');
            }

            const data = await response.json();

            if (data.success && data.accessToken) {
                // Store the demo token in localStorage
                localStorage.setItem(AUTH_TOKEN_KEY, data.accessToken);
                this.emitTelemetry('demo_session_initialized', {
                    userId: data.userId,
                    expiresIn: data.expiresIn,
                    timestamp: new Date().toISOString(),
                });

                return {
                    state: 'success',
                    data: { accessToken: data.accessToken, userId: data.userId },
                    error: null,
                };
            } else {
                throw new Error(data.error || 'Failed to initialize demo session');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            this.emitTelemetry('demo_session_error', {
                error: errorMessage,
                timestamp: new Date().toISOString(),
            });
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    /**
     * Ensure authentication is available.
     * Returns the userId if authenticated, null otherwise.
     * Does NOT fall back to demo session - user must be properly logged in.
     */
    async ensureAuth(): Promise<string | null> {
        // Check if we already have a valid token
        if (this.isAuthenticated()) {
            const token = this.getAuthToken();
            if (token) {
                try {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    return payload.userId || payload.sub || null;
                } catch {
                    // Token is malformed, clear it
                    this.logout();
                    return null;
                }
            }
        }

        // Not authenticated - return null (no demo fallback)
        return null;
    }

    async validateBetaCode(code: string): Promise<ApiResult<{ valid: boolean; message?: string }>> {
        const url = `${API_BASE_URL}/api/realsignal/auth/validate-beta-code`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ betaCode: code }),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    state: 'error',
                    data: { valid: false, message: data.error || 'Invalid beta code' },
                    error: data.error || 'Invalid beta code',
                };
            }

            return { state: 'success', data: { valid: true }, error: null };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: { valid: false, message: errorMessage },
                error: errorMessage,
            };
        }
    }

    async requestPasswordReset(email: string): Promise<ApiResult<{ message: string }>> {
        const url = `${API_BASE_URL}/api/realsignal/auth/forgot-password`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            return {
                state: 'success',
                data: {
                    message:
                        'If an account exists with this email, you will receive a password reset link.',
                },
                error: null,
            };
        } catch (error) {
            return {
                state: 'success',
                data: {
                    message:
                        'If an account exists with this email, you will receive a password reset link.',
                },
                error: null,
            };
        }
    }

    async resetPassword(
        token: string,
        newPassword: string
    ): Promise<ApiResult<{ message: string }>> {
        const url = `${API_BASE_URL}/api/realsignal/auth/reset-password`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token, newPassword }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to reset password');
            }

            const data = await response.json();

            if (data.success) {
                return {
                    state: 'success',
                    data: { message: data.data?.message || 'Password reset successfully' },
                    error: null,
                };
            } else {
                throw new Error(data.error || 'Failed to reset password');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async starProperty(
        userId: string,
        propertyId: string,
        status: string = 'interested'
    ): Promise<ApiResult<InterestedProperty>> {
        const url = `${API_BASE_URL}/api/realsignal/properties/${encodeURIComponent(propertyId)}/star`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders(),
                },
                body: JSON.stringify({ userId, status }),
            });

            if (response.status === 401) {
                this.logout();
                return {
                    state: 'authError',
                    data: null,
                    error: 'Session expired. Please sign in again.',
                };
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to star property');
            }

            const data = await response.json();

            if (data.success) {
                return {
                    state: 'success',
                    data: data.interested,
                    error: null,
                };
            } else {
                throw new Error(data.error || 'Failed to star property');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async unstarProperty(
        userId: string,
        propertyId: string
    ): Promise<ApiResult<{ success: boolean }>> {
        const url = `${API_BASE_URL}/api/realsignal/properties/${encodeURIComponent(propertyId)}/star?userId=${encodeURIComponent(userId)}`;

        try {
            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders(),
                },
            });

            if (response.status === 401) {
                this.logout();
                return {
                    state: 'authError',
                    data: null,
                    error: 'Session expired. Please sign in again.',
                };
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to unstar property');
            }

            const data = await response.json();

            if (data.success) {
                return {
                    state: 'success',
                    data: { success: true },
                    error: null,
                };
            } else {
                throw new Error(data.error || 'Failed to unstar property');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async getInterestedIds(userId: string): Promise<ApiResult<string[]>> {
        const url = `${API_BASE_URL}/api/realsignal/interested/ids?userId=${encodeURIComponent(userId)}`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders(),
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to fetch interested properties');
            }

            const data: InterestedIdsResponse = await response.json();

            if (data.success) {
                return {
                    state: 'success',
                    data: data.propertyIds,
                    error: null,
                };
            } else {
                throw new Error('Failed to fetch interested properties');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async getInterestedProperties(
        userId: string,
        limit: number = 100,
        offset: number = 0
    ): Promise<ApiResult<InterestedResponseV2>> {
        const url = `${API_BASE_URL}/api/realsignal/interested?userId=${encodeURIComponent(userId)}&limit=${limit}&offset=${offset}`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders(),
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to fetch interested properties');
            }

            const data: InterestedResponseV2 = await response.json();

            if (data.success) {
                return {
                    state: 'success',
                    data,
                    error: null,
                };
            } else {
                throw new Error('Failed to fetch interested properties');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async getPropertyEnrichment(
        propertyId: string
    ): Promise<ApiResult<PropertyEnrichmentResponse>> {
        // V1: Tenant is hardcoded server-side to 'harris' for security
        const url = `${API_BASE_URL}/api/realsignal/properties/${encodeURIComponent(propertyId)}/enrichment`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders(),
                },
            });

            if (response.status === 401) {
                return {
                    state: 'authError',
                    data: null,
                    error: 'Authentication required',
                };
            }

            if (response.status === 404) {
                return {
                    state: 'notDeployed',
                    data: null,
                    error: 'Enrichment endpoint not deployed',
                };
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to fetch enrichment status');
            }

            const data: PropertyEnrichmentResponse = await response.json();
            return { state: 'success', data, error: null };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async requestPropertyEnrichment(
        propertyId: string
    ): Promise<ApiResult<EnrichmentRequestResponse>> {
        // V1: Tenant is hardcoded server-side to 'harris' for security
        const url = `${API_BASE_URL}/api/realsignal/properties/${encodeURIComponent(propertyId)}/enrichment/request`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders(),
                },
            });

            if (response.status === 401) {
                return {
                    state: 'authError',
                    data: null,
                    error: 'Authentication required',
                };
            }

            if (response.status === 404) {
                return {
                    state: 'notDeployed',
                    data: null,
                    error: 'Enrichment endpoint not deployed',
                };
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to request enrichment');
            }

            const data: EnrichmentRequestResponse = await response.json();
            return { state: 'success', data, error: null };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async getExecutionSignals(propertyId: string): Promise<ApiResult<ExecutionSignalsResponseV1>> {
        const url = `${API_BASE_URL}/api/realsignal/properties/${encodeURIComponent(propertyId)}/execution-signals`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders(),
                },
            });

            if (response.status === 401) {
                this.handle401();
                return {
                    state: 'authError',
                    data: null,
                    error: 'Authentication required - please sign in',
                };
            }

            if (response.status === 403) {
                return {
                    state: 'authError',
                    data: null,
                    error: 'Not authorized to access this resource',
                };
            }

            if (response.status === 404) {
                return {
                    state: 'notDeployed',
                    data: null,
                    error: 'Execution signals endpoint not deployed',
                };
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to fetch execution signals');
            }

            const data: ExecutionSignalsResponseV1 = await response.json();
            return { state: 'success', data, error: null };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async updatePropertyStage(
        userId: string,
        propertyId: string,
        stage: string
    ): Promise<ApiResult<InterestedProperty>> {
        const url = `${API_BASE_URL}/api/realsignal/properties/${encodeURIComponent(propertyId)}/stage`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders(),
                },
                body: JSON.stringify({ userId, stage }),
            });

            if (response.status === 401) {
                this.handle401();
                return {
                    state: 'authError',
                    data: null,
                    error: 'Session expired. Please sign in again.',
                };
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to update property stage');
            }

            const data = await response.json();

            if (data.success) {
                return {
                    state: 'success',
                    data: data.interested,
                    error: null,
                };
            } else {
                throw new Error(data.error || 'Failed to update property stage');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async dismissProperty(
        propertyId: string,
        reason?: DismissReason
    ): Promise<ApiResult<{ success: boolean }>> {
        const userId = this.getCurrentUserId();
        const url = `${API_BASE_URL}/api/realsignal/properties/${encodeURIComponent(propertyId)}/dismiss`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders(),
                },
                body: JSON.stringify({ userId, reason: reason || undefined }),
            });

            if (response.status === 401) {
                this.handle401();
                return {
                    state: 'authError',
                    data: null,
                    error: 'Session expired. Please sign in again.',
                };
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to dismiss property');
            }

            const data = await response.json();

            if (data.success) {
                return {
                    state: 'success',
                    data: { success: true },
                    error: null,
                };
            } else {
                throw new Error(data.error || 'Failed to dismiss property');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async restoreProperty(propertyId: string): Promise<ApiResult<{ success: boolean }>> {
        const userId = this.getCurrentUserId();
        const url = `${API_BASE_URL}/api/realsignal/properties/${encodeURIComponent(propertyId)}/restore`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders(),
                },
                body: JSON.stringify({ userId }),
            });

            if (response.status === 401) {
                this.handle401();
                return {
                    state: 'authError',
                    data: null,
                    error: 'Session expired. Please sign in again.',
                };
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to restore property');
            }

            const data = await response.json();

            if (data.success) {
                return {
                    state: 'success',
                    data: { success: true },
                    error: null,
                };
            } else {
                throw new Error(data.error || 'Failed to restore property');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async getDismissedIds(): Promise<ApiResult<string[]>> {
        const userId = this.getCurrentUserId();
        if (!userId) {
            return { state: 'error', data: null, error: 'Not authenticated' };
        }
        const url = `${API_BASE_URL}/api/realsignal/dismissed/ids?userId=${encodeURIComponent(userId)}`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders(),
                },
            });

            if (response.status === 401) {
                this.handle401();
                return {
                    state: 'authError',
                    data: null,
                    error: 'Session expired. Please sign in again.',
                };
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to fetch dismissed properties');
            }

            const data: DismissedIdsResponse = await response.json();

            if (data.success) {
                return {
                    state: 'success',
                    data: data.propertyIds,
                    error: null,
                };
            } else {
                throw new Error('Failed to fetch dismissed properties');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }

    async getTaxSignals(propertyId: string): Promise<ApiResult<TaxSignalsResponseV1>> {
        const url = `${API_BASE_URL}/api/realsignal/properties/${encodeURIComponent(propertyId)}/tax-signals`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders(),
                },
            });

            if (response.status === 401) {
                this.handle401();
                return {
                    state: 'authError',
                    data: null,
                    error: 'Authentication required - please sign in',
                };
            }

            if (response.status === 403) {
                return {
                    state: 'authError',
                    data: null,
                    error: 'Not authorized to access this resource',
                };
            }

            if (response.status === 404) {
                return {
                    state: 'notDeployed',
                    data: null,
                    error: 'Tax signals endpoint not deployed',
                };
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to fetch tax signals');
            }

            const data: TaxSignalsResponseV1 = await response.json();
            return { state: 'success', data, error: null };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            return {
                state: 'error',
                data: null,
                error: errorMessage,
            };
        }
    }
}

// ============================================================================
// Execution Intelligence Contract Types (v1)
// UI-side types matching backend contract - do NOT import from backend
// @see specs/realsignal/execution_intelligence_v1.md
//
// TODO: Move these types to a shared location (e.g., src/shared/realsignal/)
// to avoid drift between UI and backend. Currently duplicated here because
// the backend types are not exposed to the UI build. Track in future cleanup.
// ============================================================================

export type ReadinessStatus = 'ready' | 'not_ready' | 'unknown';

export type ExecutionSignalType =
    | 'auction_timing'
    | 'tax_status'
    | 'title_risk'
    | 'condition'
    | 'occupancy'
    | 'valuation_gap'
    | 'portfolio_workflow'
    | 'data_quality';

export type ExecutionSignalSeverity = 'info' | 'watch' | 'action' | 'blocker';

export type ExecutionSignalStatus = 'active' | 'resolved' | 'stale';

export interface ExecutionSignalV1 {
    signalId: string;
    propertyId: string;
    signalType: ExecutionSignalType;
    severity: ExecutionSignalSeverity;
    status: ExecutionSignalStatus;
    confidence: number;
    score: number | null;
    title: string;
    summary: string;
    recommendedAction: string | null;
    blockers: string[];
    evidence: unknown[];
    lastEvaluatedAt: string;
    validUntil: string | null;
    tags: string[];
    version: 'v1';
}

export interface ExecutionSignalsResponseV1 {
    propertyId: string;
    ready: boolean;
    readinessStatus: ReadinessStatus;
    signals: ExecutionSignalV1[];
    generatedAt: string;
    version: 'v1';
    notes?: string[];
}

// ============================================================================
// Tax Intelligence Contract Types (v1)
// UI-side types matching backend contract - do NOT import from backend
// @see specs/realsignal/tax_intelligence_v1.md
//
// TODO: Move these types to a shared location (e.g., src/shared/realsignal/)
// to avoid drift between UI and backend. Currently duplicated here because
// the backend types are not exposed to the UI build. Track in future cleanup.
// ============================================================================

export type TaxSignalType =
    | 'tax_sale'
    | 'tax_delinquency'
    | 'tax_lien'
    | 'redemption_period'
    | 'tax_current';

export type TaxSignalSeverity = 'info' | 'watch' | 'action' | 'blocker';

export type TaxSignalStatus = 'active' | 'resolved' | 'stale';

export interface TaxSignalV1 {
    signalId: string;
    propertyId: string;
    signalType: TaxSignalType;
    severity: TaxSignalSeverity;
    status: TaxSignalStatus;
    confidence: number;
    title: string;
    summary: string;
    amounts: {
        totalOwed?: number;
        principal?: number;
        interest?: number;
        penalties?: number;
        fees?: number;
        redemptionAmount?: number;
    } | null;
    keyDates: {
        delinquentSince?: string;
        saleDate?: string;
        redemptionDeadline?: string;
        lastPaymentDate?: string;
    } | null;
    jurisdiction: {
        state: string;
        county: string;
        taxingAuthority?: string;
    } | null;
    recommendedAction: string | null;
    blockers: string[];
    evidence: unknown[];
    lastEvaluatedAt: string;
    validUntil: string | null;
    tags: string[];
    version: 'v1';
}

export interface TaxSignalsResponseV1 {
    propertyId: string;
    ready: boolean;
    readinessStatus: ReadinessStatus;
    signals: TaxSignalV1[];
    generatedAt: string;
    version: 'v1';
    notes?: string[];
}

// ============================================================================
// Enrichment Status Contract Types
// ============================================================================

export type EnrichmentState =
    | 'unknown'
    | 'retrieval_in_progress'
    | 'retrieved'
    | 'not_published_by_source'
    | 'source_unreachable'
    | 'requires_manual_review';

export interface FieldEnrichmentStatus {
    state: EnrichmentState;
    source?: string;
    lastAttemptAt?: string;
    updatedAt?: string;
}

export interface PropertyEnrichmentResponse {
    success: boolean;
    propertyId: string;
    fields: Record<string, FieldEnrichmentStatus>;
    tablesInitialized: boolean;
}

export interface EnrichmentRequestResponse {
    success: boolean;
    propertyId: string;
    tasksQueued: number;
    fields: Record<string, FieldEnrichmentStatus>;
    tablesInitialized: boolean;
}

export const realSignalApi = new RealSignalApiClient();
